import { useEffect, useRef, useState } from 'react'

export const scrollToElementPreview = ({ current }) => {
  /** Set state initialized to -1, because the div's height is
   * 0 when the screen has no fields
   *
   */
  /** Store the last scroll height */
  const [storedScrollHeight, setScrollHeight] = useState(-1)

  useEffect(() => {
    /** Only change if the divs height is larger than before. i.e the user added a new field */
    /** we don't want to trigger a scroll height reset when a user rearragnes fields. */

    if (current && current.scrollHeight > storedScrollHeight) {
      current.scrollTop = current.scrollHeight
      setScrollHeight(current.scrollHeight)
    }
  })
}

export const scrollToElement = ({ current }, active) => {
  useEffect(() => {
    if (active && current) {
      current.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }, [active])
}

export const useEventListener = (eventName, handler, element = window) => {
  // Create a ref that stores handler
  const savedHandler = useRef()

  // Update ref.current value if handler changes.
  // This allows our effect below to always get latest handler ...
  // ... without us needing to pass it in effect deps array ...
  // ... and potentially cause effect to re-run every render.
  useEffect(() => {
    savedHandler.current = handler
  }, [handler])

  useEffect(
    () => {
      // Make sure element supports addEventListener
      // On
      const isSupported = element && element.addEventListener
      if (!isSupported) return

      // Create event listener that calls handler function stored in ref
      const eventListener = event => savedHandler.current(event)
      element.removeEventListener(eventName, eventListener)

      // Add event listener
      element.addEventListener(eventName, eventListener)

      // Remove event listener on cleanup
      return () => {
        element.removeEventListener(eventName, eventListener)
      }
    },
    [eventName, element] // Re-run if eventName or element changes
  )
}

// Hook
export const useDebounce = (value, delay) => {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value)
      }, delay)

      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler)
      }
    },
    [value, delay] // Only re-call effect if value or delay changes
  )

  return debouncedValue
}

export const onClickOutside = (ref, action) => {
  const handleClick = e => {
    if (ref.current && ref.current.contains(e.target)) {
      // inside click
      return
    }
    // outside click
    action()
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClick)

    return () => {
      document.removeEventListener('mousedown', handleClick)
    }
  }, [ref])
}
